import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { COUNTRY_OPTIONS } from '@/lib/countries'

export type EnquiryOriginValue = {
  origin_country_code: string
  origin_locality: string
  residence_country_code: string
  residence_locality: string
}

export function emptyEnquiryOriginValue(): EnquiryOriginValue {
  return {
    origin_country_code: '',
    origin_locality: '',
    residence_country_code: '',
    residence_locality: '',
  }
}

const selectClassName =
  'min-h-11 h-11 w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface)] px-3 text-sm text-[var(--lc-text-primary)]'

function CountryField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">{label}</span>
      <select
        id={id}
        className={selectClassName}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Not set</option>
        {COUNTRY_OPTIONS.map((option) => (
          <option key={option.code} value={option.code}>
            {option.label.en}
          </option>
        ))}
      </select>
    </label>
  )
}

export function EnquiryOriginFields({
  value,
  onChange,
  idPrefix = 'enquiry-origin',
}: {
  value: EnquiryOriginValue
  onChange: (next: EnquiryOriginValue) => void
  idPrefix?: string
}) {
  const set = (key: keyof EnquiryOriginValue) => (next: string) => {
    onChange({ ...value, [key]: next })
  }
  return (
    <fieldset className="grid gap-3 sm:grid-cols-2">
      <legend className="mb-1 text-[length:var(--lc-type-body-sm)] font-medium text-[var(--lc-text-primary)]">
        Enquiry origin
      </legend>
      <p className="sm:col-span-2 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
        Where the enquirer is from, and where they live now. Leave blank if unknown.
      </p>
      <CountryField
        id={`${idPrefix}-origin-country`}
        label="Origin country"
        value={value.origin_country_code}
        onChange={set('origin_country_code')}
      />
      <label className="block space-y-1" htmlFor={`${idPrefix}-origin-locality`}>
        <span className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">Origin locality</span>
        <Input
          id={`${idPrefix}-origin-locality`}
          value={value.origin_locality}
          maxLength={200}
          placeholder="City or village"
          onChange={(event) => set('origin_locality')(event.target.value)}
          className="min-h-11"
        />
      </label>
      <CountryField
        id={`${idPrefix}-residence-country`}
        label="Residence country"
        value={value.residence_country_code}
        onChange={set('residence_country_code')}
      />
      <label className="block space-y-1" htmlFor={`${idPrefix}-residence-locality`}>
        <span className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">Residence locality</span>
        <Input
          id={`${idPrefix}-residence-locality`}
          value={value.residence_locality}
          maxLength={200}
          placeholder="City or village"
          onChange={(event) => set('residence_locality')(event.target.value)}
          className="min-h-11"
        />
      </label>
    </fieldset>
  )
}

export type EnquiryOriginPayload = {
  origin_country_code: string | null
  origin_locality: string | null
  residence_country_code: string | null
  residence_locality: string | null
}

export function toEnquiryOriginPayload(value: EnquiryOriginValue): EnquiryOriginPayload {
  return {
    origin_country_code: value.origin_country_code || null,
    origin_locality: value.origin_locality.trim() || null,
    residence_country_code: value.residence_country_code || null,
    residence_locality: value.residence_locality.trim() || null,
  }
}

export function EnquiryOriginEditor({
  conversationId,
  originCountryCode,
  originLocality,
  residenceCountryCode,
  residenceLocality,
  onSave,
}: {
  conversationId: string
  originCountryCode?: string | null
  originLocality?: string | null
  residenceCountryCode?: string | null
  residenceLocality?: string | null
  onSave: (next: EnquiryOriginPayload) => Promise<void>
}) {
  const [value, setValue] = useState<EnquiryOriginValue>(emptyEnquiryOriginValue)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setValue({
      origin_country_code: originCountryCode || '',
      origin_locality: originLocality || '',
      residence_country_code: residenceCountryCode || '',
      residence_locality: residenceLocality || '',
    })
  }, [conversationId, originCountryCode, originLocality, residenceCountryCode, residenceLocality])

  return (
    <form
      className="shrink-0 border-b border-[var(--lc-border)] bg-[var(--lc-surface-raised)] px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        setSaving(true)
        void onSave(toEnquiryOriginPayload(value))
          .finally(() => setSaving(false))
      }}
    >
      <EnquiryOriginFields
        idPrefix={`conversation-${conversationId}`}
        value={value}
        onChange={setValue}
      />
      <div className="mt-3 flex justify-end">
        <Button type="submit" size="sm" className="min-h-11" disabled={saving}>
          {saving ? <Loader2 className="me-2 h-3.5 w-3.5 animate-spin" /> : null}
          Save enquiry origin
        </Button>
      </div>
    </form>
  )
}
